import moment from 'moment';
import type { CalendarEntry, CalendarEntryWithDate } from './api/bookingsApi';
import type { Prices } from '../../features/prices/types';

interface EffectiveSelectionResult {
  effectiveArrival: moment.Moment;
  selectedBooking?: CalendarEntry;
  effectiveDurationOfStay?: number;
  effectiveOccupancy: number;
}

function arrivalFromApiResponse(prices: Prices): moment.Moment | undefined {
  if (prices.matchingSuggestion != null) {
    return moment(prices.matchingSuggestion.arrival);
  }
  return (prices.arrival && moment(prices.arrival)) || undefined;
}

function calulateDurationOfStay(prices: Prices): number | undefined {
  if (prices.matchingSuggestion != null) {
    const arrivalDate = moment(prices.matchingSuggestion.arrival);
    const departureDate = moment(prices.matchingSuggestion.departure);
    if (arrivalDate.isValid() && departureDate.isValid()) {
      return moment.duration(departureDate.diff(arrivalDate)).asDays();
    }
  }
  return prices.durationOfStay || undefined;
}

function calculateEffectiveOccupancy(
  prices: Prices,
  currentOccupancy: number,
  maxOccupancy: number,
): number {
  if (prices.maxOccupancy && prices.maxOccupancy <= maxOccupancy) {
    return prices.maxOccupancy;
  }
  if (currentOccupancy <= maxOccupancy && currentOccupancy > 0) {
    return currentOccupancy;
  }
  return currentOccupancy;
}

// eslint-disable-next-line import/prefer-default-export
export function useEffectiveSelection(
  prices: Prices | undefined,
  bookings: CalendarEntryWithDate | undefined,
  arrivalState: moment.Moment | null,
  currentDurationOfStay: number | undefined,
  currentOccupancy: number,
  maxOccupancy: number,
): EffectiveSelectionResult {
  if (prices == null) {
    return {
      effectiveArrival: arrivalState || moment(),
      selectedBooking: undefined,
      effectiveDurationOfStay: currentDurationOfStay,
      effectiveOccupancy: currentOccupancy,
    };
  }

  const effectiveArrival = arrivalFromApiResponse(prices) || arrivalState || moment();
  const selectedBooking =
    effectiveArrival && bookings ? bookings[effectiveArrival.format('YYYY-MM-DD')] : undefined;
  const effectiveDurationOfStay = calulateDurationOfStay(prices);
  const effectiveOccupancy = calculateEffectiveOccupancy(prices, currentOccupancy, maxOccupancy);
  return {
    effectiveArrival,
    selectedBooking,
    effectiveDurationOfStay,
    effectiveOccupancy,
  };
}
